import { ChevronDown, X } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import CustomScrollbar from './CustomScrollbar';

interface Option {
  id: number;
  name: string;
}

interface MultiSelectDropdownProps {
  options: Option[];
  selectedIds: number[];
  onChange: (selectedIds: number[]) => void;
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  selectedIds,
  onChange,
  placeholder = 'Select options',
  className,
  disabled = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const sortedOptions = [...options].sort((a, b) => a.name.localeCompare(b.name));

  const filteredOptions = sortedOptions.filter((option) =>
    option.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  // Auto reset when no results found
  useEffect(() => {
    if (searchTerm && filteredOptions.length === 0) {
      const timer = setTimeout(() => {
        setSearchTerm('');
        onChange([]);
      }, 100);
      return () => clearTimeout(timer);
    }
    // Return undefined when condition is not met
    return undefined;
  }, [searchTerm, filteredOptions.length, onChange]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (isOpen && event.key.length === 1) {
        setSearchTerm((prev) => prev + event.key);
      } else if (isOpen && event.key === 'Backspace') {
        setSearchTerm((prev) => prev.slice(0, -1));
      } else if (event.key === 'Escape') {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  const handleToggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      onChange([...selectedIds, id]);
    }
    setSearchTerm('');
  };

  const handleRemove = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    onChange(selectedIds.filter((selectedId) => selectedId !== id));
    setSearchTerm('');
  };

  const getSelectedOptions = () => {
    return options.filter((opt) => selectedIds.includes(opt.id));
  };

  const selectedOptions = getSelectedOptions();

  return (
    <div ref={dropdownRef} className={`relative`}>
      {/* Dropdown Button */}
      <div
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={
          className
            ? className
            : `w-full p-2 rounded-lg border bg-[#4A082D33] focus:outline-none flex items-center justify-between min-h-[42px] ${
                disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
              }`
        }
      >
        <div className="flex flex-wrap gap-1 flex-1">
          {selectedOptions.length === 0 ? (
            <span className="text-[#621C43]">{placeholder}</span>
          ) : (
            selectedOptions.map((option) => (
              <span
                key={option.id}
                className="inline-flex items-center gap-1 bg-[#621C43] text-white px-2 py-1 rounded text-sm"
              >
                {option.name}
                <button
                  type="button"
                  onClick={(e) => handleRemove(option.id, e)}
                  className="hover:bg-[#4A082D] rounded-full p-0.5"
                >
                  <X size={14} />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronDown
          size={20}
          className={`text-[#621C43] transition-transform ${isOpen ? 'transform rotate-180' : ''}`}
        />
      </div>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-[9999] w-full mt-1 bg-white border border-[#621C43] rounded-lg shadow-lg max-h-96 overflow-hidden">
          <CustomScrollbar>
            {filteredOptions.length === 0 ? (
              <div className="p-3 text-center text-gray-500">
                {searchTerm ? 'No options found' : 'No options available'}
              </div>
            ) : (
              filteredOptions.map((option) => {
                const isSelected = selectedIds.includes(option.id);
                return (
                  <label
                    key={option.id}
                    className={`flex items-center px-3 py-2 cursor-pointer hover:bg-[#F8EAF1] whitespace-nowrap ${
                      isSelected ? 'bg-[#4A082D22]' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleToggle(option.id)}
                      className="mr-2 w-4 h-4 accent-[#621C43]"
                    />
                    <span className="text-[#621C43]">{option.name}</span>
                  </label>
                );
              })
            )}
          </CustomScrollbar>
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
